import React, { useCallback, useMemo, useRef, useState } from "react";
import { Formik, FormikProps } from "formik";
import {
  AccessControl,
  AsyncSelectPickerInput,
  Button,
  ConnectionStatus,
  DatePickerInput,
  ErrorMessageField,
  HorizontalDivider,
  Icon,
  Input,
  SelectPickerInput,
} from "@shared/components";
import { Hospital, Station } from "@shared/models";
import { PERMISSION, TIMEZONE_OPTIONS } from "@shared/constants";
import { CreateStationDto, StationConnectionResult, StationFormShape } from "@containers/Station/interfaces";
import { useAllowedHospitalIds, useCheckPermission } from "@shared/hooks";
import { Option } from "@shared/interfaces";
import { GET_SDH_ID_FROM_QR_CODE_REGEXP } from "@shared/constants/regexpMatches";

import {
  getInitValues,
  createValidationSchema,
  editValidationSchemaSchema,
  getHandlers,
  prepareSubmitValue,
} from "./formHelpers";
import { TrackingTimeRangeWarning } from "../TrackingTimeRangeWarning";

import "./index.scss";

interface StationFormProps {
  isLoading: boolean;
  isConnectionsLoading: boolean;
  station: Station | null;
  hospital: Hospital | null;
  stationsConnections: Record<string, StationConnectionResult> | null;
  onChangeForm: () => void;
  onViewRawData: () => void;
  onCloseForm: () => void;
  onSubmitForm: (payload: CreateStationDto) => void;
  onRefreshConnections: (code: string) => void;
}

const StationForm: React.FunctionComponent<StationFormProps> = ({
  isLoading,
  isConnectionsLoading,
  station,
  onChangeForm,
  onViewRawData,
  onCloseForm,
  onSubmitForm,
  hospital,
  stationsConnections,
  onRefreshConnections,
}) => {
  const allowedHospitals = useAllowedHospitalIds();

  const formikRef = useRef<FormikProps<StationFormShape>>(null);
  const stationConnection = stationsConnections && station ? stationsConnections[station.station_code] : null;

  const formValues = useMemo(() => getInitValues(station, hospital ? hospital.id : null), [station, hospital]);
  const [selectedHospital, setSelectedHospital] = useState<Option | null>(formValues.hospital);
  const handlers = useMemo(() => getHandlers(selectedHospital), [selectedHospital]);
  const canCreateEditStation = useCheckPermission(station ? PERMISSION.EDIT_STATION : PERMISSION.CREATE_STATION);

  const handleChangeSelect = useCallback((field: string, value: Option | Option[] | null) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue(field, value);
    }
  }, []);

  const handleChangeHospital = useCallback((value: Option | null) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue("hospital", value);
      formikRef.current.setFieldValue("patient", null);
      setSelectedHospital(value);
    }
  }, []);

  const handleChangeQrCode = useCallback((value: string) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue("qr_code", value);
      const [, stationCode] = (value || "").match(GET_SDH_ID_FROM_QR_CODE_REGEXP) || [];
      formikRef.current.setFieldValue("station_code", stationCode || "");
    }
  }, []);

  const handleChangePatient = useCallback(
    (value: Option | null) => {
      handleChangeSelect("patient", value);
    },
    [handleChangeSelect],
  );

  return (
    <Formik
      innerRef={formikRef}
      validationSchema={station ? editValidationSchemaSchema : createValidationSchema}
      enableReinitialize={true}
      validateOnBlur={false}
      onSubmit={(values, { setSubmitting }) => {
        onSubmitForm(prepareSubmitValue(values));
        setSubmitting(false);
      }}
      initialValues={formValues}
      validate={() => {
        onChangeForm();
      }}
    >
      {({ values, handleChange, setFieldValue, handleBlur, handleSubmit }) => (
        <div className="station-form">
          <form>
            <div className="station-form-header">
              <div className="station-form-header-left">{station ? "Check SDH" : "New SDH"}</div>
              <div className="station-form-header-right">
                {station ? (
                  <div className="station-form-header-connection">
                    <ConnectionStatus
                      item={station?.station_code}
                      connections={stationsConnections}
                      isConnectionsLoading={isConnectionsLoading}
                      onRefreshConnections={onRefreshConnections}
                      placement="bottom"
                    />
                  </div>
                ) : null}
                <AccessControl permission={station ? PERMISSION.EDIT_STATION : PERMISSION.CREATE_STATION}>
                  <Button onClick={() => handleSubmit()} className="action-btn" loading={isLoading}>
                    {station ? "Save" : "Create"}
                  </Button>
                </AccessControl>
                <Button className="close-btn" onClick={() => onCloseForm()}>
                  <Icon type="clear" />
                </Button>
              </div>
            </div>
            <div className="station-form-content">
              {!station?.patient && allowedHospitals.hasAccessToMultipleHospitals && (
                <div className="station-id-row">
                  <AsyncSelectPickerInput
                    disabled={!canCreateEditStation}
                    labelTitle="Hospital*"
                    name="hospital"
                    className="select-picker"
                    onChange={handleChangeHospital}
                    value={values.hospital}
                    placeholder="Hospital*"
                    getData={handlers.hospital.getData}
                    selectData={handlers.hospital.selectData}
                    prepareOptionFunction={handlers.hospital.prepareOptionFunction}
                    loadingAction={handlers.hospital.loadingAction}
                  />
                  <ErrorMessageField name="hospital" />
                </div>
              )}
              <div className="station-id-row">
                <Input
                  disabled={!canCreateEditStation}
                  type="text"
                  labelTitle="QR Code"
                  name="qr_code"
                  onChange={(v: React.FormEvent<HTMLInputElement>) => handleChangeQrCode(v.currentTarget.value)}
                  onBlur={handleBlur}
                  value={values.qr_code}
                  placeholder="QR Code"
                />
                <ErrorMessageField name="qr_code" />
              </div>
              <div className="station-id-row">
                <Input
                  disabled={!canCreateEditStation || Boolean(values.qr_code)}
                  type="text"
                  labelTitle="SDH ID*"
                  name="station_code"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.station_code}
                  placeholder="SDH ID"
                />
                <ErrorMessageField name="station_code" />
              </div>
              <div className="station-patient-row">
                <AsyncSelectPickerInput
                  disabled={!canCreateEditStation}
                  labelTitle="Patient"
                  name="patient"
                  className="select-picker"
                  onChange={handleChangePatient}
                  value={values.patient}
                  placeholder="Patient"
                  getData={handlers.patient.getData}
                  selectData={handlers.patient.selectData}
                  prepareOptionFunction={handlers.patient.prepareOptionFunction}
                  loadingAction={handlers.patient.loadingAction}
                />
                <ErrorMessageField name="patient" />
              </div>
              <HorizontalDivider />
              <div className="tracking-time-range-row">
                <TrackingTimeRangeWarning stationConnection={stationConnection} isLoading={isConnectionsLoading} />
                <div className="group-label tracking-time-range">Tracking time Range</div>
                <div className="group-label">Please set up the time according to the patient&apos;s timezone.</div>
                <div className="tracking-time-range-content">
                  <div className="tracking-time-range-input">
                    <DatePickerInput
                      disabled={!canCreateEditStation}
                      value={values.start_time}
                      onChange={(v) => setFieldValue("start_time", v)}
                      labelTitle="Start Time"
                      labelClassName="patient-details-dp-label"
                      format="hh:mm aa"
                      showMeridian
                      placement="auto"
                    />
                    <ErrorMessageField name="start_time" fullWidth />
                  </div>
                  <div className="tracking-time-range-input">
                    <DatePickerInput
                      disabled={!canCreateEditStation}
                      value={values.end_time}
                      onChange={(v) => setFieldValue("end_time", v)}
                      labelTitle="End Time"
                      labelClassName="patient-details-dp-label"
                      format="hh:mm aa"
                      showMeridian
                      placement="auto"
                    />
                    <ErrorMessageField name="end_time" />
                  </div>
                  <div className="tracking-time-range-input">
                    <SelectPickerInput
                      disabled={!canCreateEditStation}
                      searchable
                      name="timezone"
                      data={TIMEZONE_OPTIONS}
                      onChange={(value) => setFieldValue("timezone", value)}
                      onClean={handleChange}
                      labelTitle="Time Zone"
                      value={values.timezone}
                      placeholder="Time Zone"
                      placement="auto"
                    />
                    <ErrorMessageField name="timezone" />
                  </div>
                </div>
              </div>
              <HorizontalDivider />
              <div className="station-patient-row">
                {station && (
                  <Input
                    disabled={!canCreateEditStation}
                    type="text"
                    labelTitle="Firmware Version"
                    name="firmware_version"
                    value={values.firmware_version}
                    onChange={handleChange}
                  />
                )}
              </div>
            </div>
          </form>
          <AccessControl permission={PERMISSION.DELETE_SATION}>
            {station ? (
              <div className="station-form-footer">
                <Button loading={isLoading} className="activate" onClick={onViewRawData}>
                  Test
                </Button>
              </div>
            ) : null}
          </AccessControl>
        </div>
      )}
    </Formik>
  );
};

export default StationForm;
